import { SVGProps } from 'react';

import {
  ai_chip_maker_database_path,
  big_tech_probe_path,
  cloud_database_path,
  corporate_landing_path,
  create_company_index_path,
  edit_user_team_settings_path,
  enterprise_tech_power_list_path,
  events_path,
  feed_path,
  forum_path,
  generative_ai_database_path,
  gift_landing_path,
  gigafactory_database_path,
  highlight_path,
  kids_tech_survey_path,
  logout_path,
  newsletter_list_path,
  opinion_feed_path,
  org_charts_path,
  partners_list_path,
  referrals_path,
  search_page_path,
  settings_path,
  static_jobs_path,
  subscriber_directories_path,
  subscriber_profile_path,
  tech_ipo_tracker_path,
  ti50_database_path,
  ti_50_list_current_path,
  topic_landing_path,
  vc_diversity_index_path,
  video_library_path,
} from '@routes';

import SvgIconFacebook from '../Images/Rebranding/IconFacebook';
import SvgIconInstagram from '../Images/Rebranding/IconInstagram';
import SvgIconLinkedIn from '../Images/Rebranding/IconLinkedIn';
import SvgThreads from '../Images/Rebranding/IconThreads';
import SvgIconX from '../Images/Rebranding/IconX';

export type NavItem = {
  name: string;
  href: string;
  description?: string;
  icon?: (props: SVGProps<SVGSVGElement>) => React.JSX.Element;
  hasExternalLink?: boolean;
};

export const PRO_SUBMENU_NAV_ITEMS: NavItem[] = [
  {
    name: 'Org Charts',
    href: org_charts_path(),
  },
  {
    name: 'Generative AI Database',
    href: generative_ai_database_path(),
  },
  {
    name: 'AI Chip Database',
    href: ai_chip_maker_database_path(),
  },
  {
    name: 'Creator Economy Database',
    href: create_company_index_path(),
  },
  {
    name: 'Cloud Database',
    href: cloud_database_path(),
  },
  {
    name: 'Tech IPO Tracker',
    href: tech_ipo_tracker_path(),
  },
  {
    name: 'Pro Newsletter',
    href: highlight_path('pro-weekly'),
  },
  {
    name: 'Gigafactory Database',
    href: gigafactory_database_path(),
  },
  {
    name: 'The Information 50',
    href: ti_50_list_current_path(),
  },
];

export const PRO_SPECIAL_SUBMENU_NAV_ITEMS: NavItem[] = [
  {
    name: 'The Information 50 Database',
    href: ti50_database_path(),
  },
  {
    name: 'VC Diversity Index',
    href: vc_diversity_index_path(),
  },
  {
    name: 'Enterprise Tech Powerlist',
    href: enterprise_tech_power_list_path(),
  },
  {
    name: 'Big Tech Probe Tracker',
    href: big_tech_probe_path(),
  },
  {
    name: 'Kids and Technology Survey',
    href: kids_tech_survey_path(),
  },
];

export const COMMUNITY_SUBMENU_NAV_ITEMS: NavItem[] = [
  {
    name: 'Events',
    description: 'Join us at future in-person and virtual events.',
    href: events_path(),
  },
  {
    name: 'Directory',
    description: 'Search, find and engage with others who are serious about tech and business.',
    href: subscriber_directories_path(),
  },
  {
    name: 'Forum',
    description: 'Follow and be a part of discussions about tech, finance and media.',
    href: forum_path(),
  },
];

export const MORE_SUBMENU_MAIN_NAV_ITEMS: NavItem[] = [
  {
    name: 'Brand Partnerships',
    description: 'Premium advertising opportunities for brands',
    href: partners_list_path(),
  },
  {
    name: 'Corporate Opportunities',
    description: 'Team access to our exclusive tech news',
    href: corporate_landing_path(),
  },
  {
    name: 'Newsletters',
    description: 'Journalists who break and shape the news, in your inbox',
    href: newsletter_list_path(),
  },
  {
    name: 'Video',
    description: 'Catch up on conversations with global leaders in tech, media and finance',
    href: video_library_path(),
  },
];

export const MORE_SUBMENU_SOCIAL_NAV_ITEMS: NavItem[] = [
  {
    name: 'X',
    href: 'https://twitter.com/theinformation',
    icon: SvgIconX,
  },
  {
    name: 'Facebook',
    href: 'https://www.facebook.com/gettheinformation',
    icon: SvgIconFacebook,
  },
  {
    name: 'LinkedIn',
    href: 'https://www.linkedin.com/company/theinformation',
    icon: SvgIconLinkedIn,
  },
  {
    name: 'Threads',
    href: 'https://www.threads.net/@theinformation',
    icon: SvgThreads,
  },
  {
    name: 'Instagram',
    href: 'https://www.instagram.com/theinformation/',
    icon: SvgIconInstagram,
  },
];

export const MORE_SUBNAV_FOOTER_NAV_ITEMS: NavItem[] = [
  {
    name: 'Help & Support',
    href: 'https://theinformation.zendesk.com/hc/en-us',
    hasExternalLink: true,
  },
  {
    name: 'RSS Feed',
    href: feed_path(),
  },
  {
    name: 'Careers',
    href: static_jobs_path(),
  },
];

export const SETTINGS_SUBNAV_MAIN_NAV_ITEMS = (username: string): NavItem[] => [
  {
    name: 'Profile',
    href: subscriber_profile_path(username),
  },
  {
    name: 'Settings',
    href: settings_path(),
  },
  {
    name: 'Sign out',
    href: logout_path(),
  },
];

export const SETTINGS_SUBNAV_MORE_NAV_ITEMS: NavItem[] = [
  {
    name: 'Refer a friend',
    href: referrals_path(),
  },
  {
    name: 'Give a subscription',
    href: gift_landing_path(),
  },
  {
    name: 'Set up a team',
    href: edit_user_team_settings_path(),
  },
];

export const MOBILE_SIDE_NAV_ITEMS: NavItem[] = [
  {
    name: 'Search',
    href: search_page_path(),
  },
  {
    name: 'Opinion',
    href: opinion_feed_path(),
  },
  {
    name: 'Venture Capital',
    href: topic_landing_path('venture-capital'),
  },
  {
    name: 'Creator Economy',
    href: topic_landing_path('creator-economy'),
  },
  {
    name: 'Artificial Intelligence',
    href: topic_landing_path('artificial-intelligence'),
  },
  {
    name: 'Startups',
    href: topic_landing_path('startups'),
  },
  {
    name: 'Market Research',
    href: topic_landing_path('market-research'),
  },
];
